// 1-C — merge_slots matchKey row-level timestamp/date 차단 + entity-level identifier auto-swap.
//
// 문제 패턴 (L5-COMM-MESSAGE-001 / L5-SALES-LINKOPAY-001):
//   사용자: "3월 4월 5월 문자 발송 비교" → 3 call_api(CHECK_MESSAGE_LIST_BY+PERIOD, slot=compare_1/2/base) 정상
//   → merge_slots(matchKey:'createdDate', mode:'outer') — createdDate는 row-level timestamp
//   → outer merge keyUniverse = 모든 row (unique timestamp) → 행 폭발 (65건 발송일 펼침)
//   → 비교 컬럼 (3월_count/4월_count/5월_count) 미생성, 비교 의미 상실
//
// 대비 (정상): L5-COMM-BIZ-001 matchKey:'title' (entity-level 메시지 종류) — outer merge 시 47 entity 비교 컬럼 정상
//
// 본질 차단:
//   mergeSlots 진입 시 resolveMatchKey 결과 matchKey가 row-level timestamp/date 감지
//   + outer mode + 슬롯 dateRange 다름 (multi-period) → entity-level identifier 자동 추출 + matchKey 치환.
//   priority: memberId > userId > title > itemName > productId > lessonId > name > id
//   per-call 1회 자동 swap 한정 (무한 재귀 차단).

const HEADER = '[MERGE MATCHKEY GUARD]';

// row-level timestamp/date 필드 이름 패턴 (case-insensitive).
// LLM이 흔히 선택하는 row-level 식별자 — outer merge 시 행 폭발 원인.
const ROW_LEVEL_TIMESTAMP_KEYS = new Set([
  'createddate', 'approvaldate', 'sentdate', 'registerdate', 'paymentdate',
  'reservationdate', 'startdate', 'enddate', 'timestamp', 'createdat', 'updatedat',
  'date', 'time', 'datetime',
]);

// entity-level identifier 우선순위 (사람/회원 > entity 이름 > 일반 id).
// buffer item 첫 row keys에서 본 순서대로 찾기 — 첫 매칭 사용.
const ENTITY_KEY_PRIORITY = Object.freeze([
  'memberId', 'userId', 'memberShipId', 'productId', 'itemId', 'lessonId', 'workoutId',
  'title', 'itemName', 'memberShipName', 'productName', 'lessonName', 'workoutName', 'coachName',
  'name', 'memberName', 'id',
]);

/**
 * matchKey 필드명 + 샘플 값 + cardinality로 row-level identifier 감지.
 *
 * 3가지 신호 (OR):
 *  (a) 필드명이 ROW_LEVEL_TIMESTAMP_KEYS 또는 *Date/At/Time suffix
 *  (b) 샘플 값이 ISO datetime / YYYYMMDD 형식
 *  (c) cardinality ≈ 1.0 — 슬롯의 모든 items가 unique matchKey value (entity-level이면 < 1.0)
 *      ENTITY_KEY_PRIORITY에 명시된 identifier는 제외 (memberId 등 entity-level은 항상 unique 가능)
 *
 * @param {object} ctx
 * @param {string} ctx.matchKey
 * @param {*} ctx.sampleValue
 * @param {Array<{slotName:string}>} [ctx.slots]
 * @param {Map<string,object>} [ctx.buffers]
 * @returns {boolean}
 */
export function detectTimestampMatchKey(ctx = {}) {
  const { matchKey, sampleValue, slots, buffers } = ctx;
  if (!matchKey || typeof matchKey !== 'string') return false;

  // (a) 필드명 패턴 매칭
  const lower = matchKey.toLowerCase();
  if (ROW_LEVEL_TIMESTAMP_KEYS.has(lower)) return true;
  if (/(?:Date|At|Time)$/.test(matchKey)) return true;

  // (b) 샘플 값이 ISO datetime / YYYYMMDD / YYYY-MM-DD 패턴 — _isDateString과 동일 로직
  if (typeof sampleValue === 'string') {
    if (/^(19|20)\d{2}-\d{2}-\d{2}/.test(sampleValue)) return true;
    if (/^(19|20)\d{2}\d{2}\d{2}$/.test(sampleValue)) return true;
  }

  // (c) cardinality ≈ 1.0 — matchKey가 ENTITY_KEY_PRIORITY 외이고 모든 slot에서 unique == items.length
  //     entity-level identifier (memberId/userId/title/itemName 등)는 제외 — 본질 row-level 아님
  if (!ENTITY_KEY_PRIORITY.includes(matchKey) && Array.isArray(slots) && buffers) {
    let allUnique = true;
    let totalItems = 0;
    for (const s of slots) {
      const buf = buffers.get?.(s.slotName);
      if (!buf?.items?.length) continue;
      const itemsArr = buf.items;
      totalItems += itemsArr.length;
      const uniqueValues = new Set(itemsArr.map(it => it?.[matchKey]).filter(v => v != null && v !== ''));
      // unique == items 수이면 cardinality 1.0 (모든 row가 unique matchKey)
      if (uniqueValues.size < itemsArr.length) {
        allUnique = false;
        break;
      }
    }
    // 모든 slot에서 cardinality 1.0 + 총 items 2개 이상 (single-row summary 제외)
    if (allUnique && totalItems >= 2) return true;
  }

  return false;
}

/**
 * buffer slot first row keys 분석 → entity-level identifier 추출.
 * priority 순서대로 찾기 — 첫 매칭 반환. 없으면 null.
 *
 * @param {object} ctx
 * @param {Array<{slotName:string}>} ctx.slots
 * @param {Map<string, {items:Array<object>}>} ctx.buffers
 * @returns {string | null}
 */
export function suggestEntityMatchKey(ctx = {}) {
  const { slots, buffers } = ctx;
  if (!Array.isArray(slots) || !buffers) return null;

  // 각 slot first row keys 집합 — 공통 key만 후보
  const slotKeySets = slots
    .map(s => buffers.get?.(s.slotName))
    .filter(buf => buf?.items?.length > 0)
    .map(buf => new Set(Object.keys(buf.items[0])));
  if (slotKeySets.length === 0) return null;

  // 모든 slot에 공통으로 존재하는 key 추출
  const commonKeys = [...slotKeySets[0]].filter(k =>
    slotKeySets.every(s => s.has(k))
  );

  for (const candidate of ENTITY_KEY_PRIORITY) {
    if (commonKeys.includes(candidate)) return candidate;
  }
  return null;
}

/**
 * mergeSlots 진입 시 matchKey timestamp 감지 + entity-level alt 제안.
 *
 * @param {object} ctx
 * @param {string} ctx.matchKey            — resolveMatchKey 결정 값
 * @param {string} ctx.mode                — 'inner'|'outer'|'left'|'right'|'compare'|'intersect'
 * @param {Array<{slotName:string, label?:string}>} ctx.slots
 * @param {Map<string, object>} ctx.buffers
 * @param {boolean} ctx.swapAttempted      — 본 호출에서 이미 swap 시도 (무한 재귀 차단)
 * @returns {{
 *   swap: boolean,
 *   suggestedMatchKey?: string,
 *   originalMatchKey?: string,
 *   message?: string,
 * }}
 */
export function evaluateMergeMatchKey(ctx = {}) {
  const { matchKey, mode, slots, buffers, swapAttempted } = ctx;
  if (swapAttempted) return { swap: false };
  // outer/compare 외 모드 (inner/intersect/left/right)는 본질 결함 영향 X — pass
  if (mode !== 'outer' && mode !== 'compare') return { swap: false };

  // 슬롯 dateRange 분석 — multi-period (서로 다른 dateRange 2+) 인지 확인
  const dateRanges = slots
    .map(s => buffers.get?.(s.slotName)?.dateRange)
    .filter(dr => dr && dr.startDate && dr.endDate);
  const uniqueRanges = new Set(dateRanges.map(dr => `${dr.startDate}~${dr.endDate}`));
  if (uniqueRanges.size < 2) return { swap: false };  // single-period merge — 본질 결함 영향 X

  // 첫 slot first row의 matchKey 샘플 값
  const firstSlotBuf = buffers.get?.(slots[0]?.slotName);
  const sampleValue = firstSlotBuf?.items?.[0]?.[matchKey];

  // detection: 필드명 + 샘플 값 + cardinality (slots/buffers 전달로 cardinality check 활성화)
  if (!detectTimestampMatchKey({ matchKey, sampleValue, slots, buffers })) return { swap: false };

  const suggested = suggestEntityMatchKey({ slots, buffers });
  if (!suggested || suggested === matchKey) return { swap: false };

  return {
    swap: true,
    suggestedMatchKey: suggested,
    originalMatchKey: matchKey,
    message: (
      `${HEADER} matchKey="${matchKey}"은(는) row-level timestamp/date 식별자 — outer/compare merge 시 ` +
      `각 row가 unique value라 keyUniverse가 모든 row가 되어 행 폭발 + 비교 컬럼 미생성. ` +
      `entity-level identifier "${suggested}"로 자동 치환 (auto-swap).`
    ),
  };
}

export const _HEADER = HEADER;
export const _ROW_LEVEL_TIMESTAMP_KEYS = ROW_LEVEL_TIMESTAMP_KEYS;
export const _ENTITY_KEY_PRIORITY = ENTITY_KEY_PRIORITY;
